import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Users, User as UserIcon, Trophy, Clock, Share, X, CheckCircle, Paperclip, Camera, FilePdf, FileText, Image as ImageIcon, DownloadSimple } from "@phosphor-icons/react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/")) return `${API_URL}${u}`;
  return u;
};
const humanSize = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * GroupsManager — round-robin group stage tables for tournaments AND leagues.
 * Format matches the shared "Torneo Jetour · Grupo D" print image:
 *   - Blue header, blacked-out diagonal cells (can't play yourself)
 *   - Nombre column with 1 (individual) or 2 (pairs) lines of players
 *   - Score cells with a-sets / b-sets or "vs" placeholder
 *   - CLASIFICACIÓN column showing final ranking
 *   - Match schedule table below (round_key + date/time)
 *
 * Props:
 *   eventKind: 'tournament' | 'league'
 *   eventId:   uuid
 *   eventName?: string  (for shareable header)
 *   category?: string   (default category to prefill)
 */
export default function GroupsManager({ eventKind, eventId, eventName = "", category: defaultCategory = "" }) {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // group being edited
  const [scoring, setScoring] = useState(null); // { group, match }

  const canManage = user?.role === "admin" || user?.role === "directivo";
  const eventPath = eventKind === "tournament" ? "tournaments" : "leagues";

  const load = async () => {
    setLoading(true);
    try {
      const [g, u] = await Promise.all([
        api.get(`/${eventPath}/${eventId}/groups`),
        api.get("/users"),
      ]);
      setGroups(g.data || []);
      setUsers(u.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar grupos");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (eventId) load(); /* eslint-disable-next-line */ }, [eventId, eventKind]);

  return (
    <div className="space-y-6" data-testid="groups-manager">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#CCFF00] font-bold flex items-center gap-1.5">
            <Trophy size={12} weight="fill" /> Fase de Grupos · Round-Robin
          </p>
          <h2 className="font-['Barlow_Condensed'] text-3xl font-black uppercase tracking-tighter">Grupos</h2>
          <p className="text-xs text-gray-500 mt-1">Todos contra todos. Modo pareja o individual. Formato compartido tipo cuadrícula.</p>
        </div>
        {canManage && (
          <Button
            onClick={() => setCreating(true)}
            className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide"
            data-testid="groups-create-btn"
          >
            <Plus size={16} className="mr-1" /> Nuevo grupo
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8"><div className="w-6 h-6 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>
      ) : groups.length === 0 ? (
        <div className="card-tactical p-8 text-center" data-testid="groups-empty">
          <Users size={32} className="mx-auto text-gray-600 mb-2" />
          <p className="text-sm text-gray-400">Aún no hay grupos. {canManage ? "Crea el primero con el botón 'Nuevo grupo'." : ""}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map((g) => (
            <GroupCard
              key={g.id}
              group={g}
              eventName={eventName}
              canManage={canManage}
              onEdit={() => setEditing(g)}
              onDelete={async () => {
                if (!window.confirm(`¿Eliminar grupo ${g.name}?`)) return;
                try { await api.delete(`/groups/${g.id}`); toast.success("Grupo eliminado"); load(); }
                catch (e) { toast.error(e.response?.data?.detail || "Error"); }
              }}
              onScore={(m) => setScoring({ group: g, match: m })}
              onSchedule={async (matches) => {
                try { await api.put(`/groups/${g.id}`, { matches }); toast.success("Calendario guardado"); load(); }
                catch (e) { toast.error(e.response?.data?.detail || "Error"); }
              }}
              onUpload={async (file) => {
                const fd = new FormData();
                fd.append("file", file);
                try {
                  await api.post(`/groups/${g.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                  toast.success("Adjunto guardado");
                  load();
                } catch (e) { toast.error(e.response?.data?.detail || "Error al subir"); }
              }}
              onDeleteAttachment={async (aid) => {
                if (!window.confirm("¿Eliminar adjunto?")) return;
                try { await api.delete(`/groups/${g.id}/attachments/${aid}`); toast.success("Adjunto eliminado"); load(); }
                catch (e) { toast.error(e.response?.data?.detail || "Error"); }
              }}
            />
          ))}
        </div>
      )}

      {creating && (
        <GroupFormDialog
          open={creating}
          onClose={() => setCreating(false)}
          onSubmit={async (payload) => {
            try {
              await api.post(`/${eventPath}/${eventId}/groups`, payload);
              toast.success("Grupo creado");
              setCreating(false);
              load();
            } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
          }}
          users={users}
          defaultCategory={defaultCategory}
        />
      )}
      {editing && (
        <GroupFormDialog
          open={!!editing}
          onClose={() => setEditing(null)}
          initial={editing}
          onSubmit={async (payload) => {
            try {
              await api.put(`/groups/${editing.id}`, payload);
              toast.success("Grupo actualizado");
              setEditing(null);
              load();
            } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
          }}
          users={users}
          defaultCategory={editing.category || defaultCategory}
        />
      )}
      {scoring && (
        <ScoreDialog
          open={!!scoring}
          onClose={() => setScoring(null)}
          group={scoring.group}
          match={scoring.match}
          onSaved={() => { setScoring(null); load(); }}
        />
      )}
    </div>
  );
}

// ============================================================
// GroupCard — the printable-looking round-robin table
// ============================================================
function GroupCard({ group, eventName, canManage, onEdit, onDelete, onScore, onSchedule, onUpload, onDeleteAttachment }) {
  const n = group.entries.length;
  const seeds = group.entries;
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [previewAtt, setPreviewAtt] = useState(null);
  const matchMap = useMemo(() => {
    const m = {};
    for (const x of group.matches) {
      const key = `${Math.min(x.a_seed, x.b_seed)}v${Math.max(x.a_seed, x.b_seed)}`;
      m[key] = x;
    }
    return m;
  }, [group.matches]);

  const share = async () => {
    const text = buildShareText(group, eventName);
    try {
      if (navigator.share) {
        await navigator.share({ title: `Grupo ${group.name} · ${eventName || ""}`, text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Copiado al portapapeles");
      }
    } catch { /* user cancelled */ }
  };

  const [editingSchedule, setEditingSchedule] = useState(false);
  const [schedule, setSchedule] = useState(group.matches.map((m) => ({ ...m })));

  useEffect(() => { setSchedule(group.matches.map((m) => ({ ...m }))); }, [group.matches]);

  return (
    <div className="card-tactical overflow-hidden" data-testid={`group-card-${group.name}`}>
      {/* Header: TORNEO / LIGA + CATEGORIA + GRUPO */}
      <div className="p-4 flex items-center justify-between gap-3 border-b border-white/10 flex-wrap bg-black/40">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] tracking-[0.25em] uppercase text-gray-500">{eventName || (group.event_kind === "tournament" ? "Torneo" : "Liga")}</p>
          <div className="flex items-center gap-2 flex-wrap mt-0.5">
            {group.category && (
              <span className="px-3 py-0.5 bg-[#1E40AF] text-white text-xs uppercase tracking-widest font-black">
                Categoría: {group.category}
              </span>
            )}
            <span className="px-3 py-0.5 bg-[#1E40AF] text-white text-xs uppercase tracking-widest font-black" data-testid={`group-header-${group.name}`}>
              Grupo: {group.name}
            </span>
            <Badge variant="outline" className="text-[10px] uppercase h-5 border-white/15 text-gray-300">
              {group.mode === "pairs" ? <><Users size={10} className="mr-1" /> Parejas</> : <><UserIcon size={10} className="mr-1" /> Individual</>}
            </Badge>
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          <Button variant="outline" size="sm" onClick={share} className="border-white/15 text-gray-300 hover:text-[#CCFF00] h-8 text-[10px] uppercase" data-testid={`group-share-${group.name}`}>
            <Share size={12} className="mr-1" /> Compartir
          </Button>
          {canManage && (
            <>
              <Button variant="outline" size="sm" onClick={onEdit} className="border-white/15 text-gray-300 hover:text-[#CCFF00] h-8 text-[10px] uppercase" data-testid={`group-edit-${group.name}`}>
                <PencilSimple size={12} className="mr-1" /> Editar
              </Button>
              <Button variant="outline" size="sm" onClick={onDelete} className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 text-[10px] uppercase" data-testid={`group-delete-${group.name}`}>
                <Trash size={12} />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Round-robin grid */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px] text-sm border-collapse" data-testid={`group-table-${group.name}`}>
          <thead>
            <tr>
              <th className="bg-[#1E40AF] text-white uppercase text-xs font-black tracking-widest border border-black/60 py-2 w-14">No.</th>
              <th className="bg-[#1E40AF] text-white uppercase text-xs font-black tracking-widest border border-black/60 py-2 text-left px-3">Nombre</th>
              {seeds.map((s) => (
                <th key={s.seed} className="bg-[#1E40AF] text-white uppercase text-xs font-black tracking-widest border border-black/60 py-2 w-16 text-center">{s.seed}</th>
              ))}
              <th className="bg-[#1E40AF] text-white uppercase text-xs font-black tracking-widest border border-black/60 py-2 w-32">Clasificación</th>
            </tr>
          </thead>
          <tbody>
            {seeds.map((e) => {
              const rankRow = group.standings.find((s) => s.seed === e.seed);
              return (
                <tr key={e.seed} className="bg-white text-black">
                  <td className="border border-black/60 text-center font-bold bg-gray-200 w-14">{e.seed}.</td>
                  <td className="border border-black/60 px-3 py-2 font-bold uppercase text-xs sm:text-sm leading-tight">
                    {(e.players && e.players.length > 0) ? (
                      <div className="flex flex-col gap-0.5">
                        {e.players.map((p, i) => (
                          <span key={i} className="flex items-center gap-1.5 flex-wrap">
                            {p.name || "—"}
                            {p.guest && (
                              <>
                                <span className="text-[8px] px-1 py-0 bg-[#FFD24A] text-black font-black uppercase tracking-widest" title="Jugador invitado (no registrado)">Inv</span>
                                {canManage && (
                                  <a
                                    href={`/register?name=${encodeURIComponent(p.name || "")}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[8px] px-1 py-0 bg-[#CCFF00] text-black font-black uppercase tracking-widest hover:bg-[#B3E600] normal-case"
                                    title="Convertir a jugador registrado (abre registro con el nombre prellenado)"
                                    data-testid={`group-guest-register-${e.seed}-${i}`}
                                  >
                                    Registrar
                                  </a>
                                )}
                              </>
                            )}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span>{e.display_name || "—"}</span>
                    )}
                  </td>
                  {seeds.map((c) => {
                    if (c.seed === e.seed) {
                      return <td key={c.seed} className="bg-black border border-black/60" />;
                    }
                    const key = `${Math.min(e.seed, c.seed)}v${Math.max(e.seed, c.seed)}`;
                    const m = matchMap[key];
                    const played = m?.status === "played";
                    // Show this row's sets on the left/right
                    const isA = m?.a_seed === e.seed;
                    const mySets = played ? (isA ? m.a_sets : m.b_sets) : null;
                    const oppSets = played ? (isA ? m.b_sets : m.a_sets) : null;
                    const iWon = played && mySets > oppSets;
                    return (
                      <td
                        key={c.seed}
                        className={`border border-black/60 text-center text-xs sm:text-sm font-bold cursor-${canManage ? "pointer" : "default"} ${played ? (iWon ? "bg-[#CCFF00]/70" : "bg-red-100") : "bg-white hover:bg-gray-100"}`}
                        onClick={() => canManage && onScore(m)}
                        data-testid={`group-cell-${group.name}-${e.seed}-${c.seed}`}
                      >
                        {played ? (
                          <div className="flex flex-col leading-none py-1">
                            <span className="text-base">{mySets}-{oppSets}</span>
                            {m.sets && m.sets.length > 0 && (
                              <span className="text-[9px] text-gray-600 mt-0.5">
                                {m.sets.map((s) => (isA ? `${s.a}-${s.b}` : `${s.b}-${s.a}`)).join(", ")}
                              </span>
                            )}
                          </div>
                        ) : canManage ? (
                          <span className="text-gray-400">vs</span>
                        ) : ""}
                      </td>
                    );
                  })}
                  <td className="border border-black/60 text-center bg-gray-100">
                    <div className="flex flex-col items-center py-1 leading-tight">
                      <span className="font-black text-lg">#{rankRow?.position || "—"}</span>
                      <span className="text-[9px] text-gray-600">{rankRow?.pts || 0} pts · {rankRow?.pg || 0}G-{rankRow?.pp || 0}P</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Schedule table */}
      <div className="p-4 bg-black/30">
        <div className="flex items-center justify-between gap-2 mb-2">
          <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#CCFF00] flex items-center gap-1"><Clock size={12} weight="fill" /> Calendario de Partidos</p>
          {canManage && (
            <Button variant="outline" size="sm" onClick={() => {
              if (editingSchedule) {
                onSchedule(schedule);
              }
              setEditingSchedule(!editingSchedule);
            }} className="border-white/15 text-gray-300 hover:text-[#CCFF00] h-7 text-[10px] uppercase" data-testid={`group-schedule-edit-${group.name}`}>
              {editingSchedule ? <><CheckCircle size={12} className="mr-1" /> Guardar</> : <><PencilSimple size={12} className="mr-1" /> Editar</>}
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[420px] text-xs">
            <thead>
              <tr>
                <th className="bg-black text-white uppercase text-[10px] font-black tracking-widest border border-white/20 py-1.5 w-24">Partidos</th>
                <th className="bg-black text-white uppercase text-[10px] font-black tracking-widest border border-white/20 py-1.5">Horario</th>
              </tr>
            </thead>
            <tbody>
              {schedule.map((m, idx) => (
                <tr key={m.round_key} className="bg-white text-black">
                  <td className="border border-white/20 text-center font-bold uppercase text-xs py-1.5">{m.a_seed} vs {m.b_seed}</td>
                  <td className="border border-white/20 text-center uppercase text-xs py-1">
                    {editingSchedule ? (
                      <input
                        type="datetime-local"
                        value={m.scheduled_at ? m.scheduled_at.slice(0, 16) : ""}
                        onChange={(e) => {
                          const next = [...schedule];
                          next[idx] = { ...m, scheduled_at: e.target.value || null };
                          setSchedule(next);
                        }}
                        className="bg-white text-black border border-gray-300 px-2 py-1 text-xs w-full text-center"
                        data-testid={`group-schedule-input-${group.name}-${m.round_key}`}
                      />
                    ) : m.scheduled_at ? (
                      new Date(m.scheduled_at).toLocaleString("es-MX", { weekday: "short", hour: "2-digit", minute: "2-digit" }).toUpperCase()
                    ) : (
                      <span className="text-gray-400 normal-case">— sin horario —</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Adjuntos: fotos + archivos de información del grupo */}
      <div className="p-4 border-t border-white/10 bg-black/20" data-testid={`group-attachments-${group.name}`}>
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#CCFF00] flex items-center gap-1">
            <Paperclip size={12} weight="fill" /> Información Adjunta
            {(group.attachments || []).length > 0 && (
              <span className="ml-1 text-gray-400 normal-case tracking-normal">({(group.attachments || []).length})</span>
            )}
          </p>
          {canManage && (
            <div className="flex gap-1.5">
              <Button
                variant="outline"
                size="sm"
                onClick={() => cameraInputRef.current?.click()}
                disabled={uploading}
                className="border-[#FFD24A]/40 text-[#FFD24A] hover:bg-[#FFD24A]/10 h-8 text-[10px] uppercase"
                data-testid={`group-attach-camera-${group.name}`}
              >
                <Camera size={12} className="mr-1" /> Tomar foto
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="border-[#CCFF00]/40 text-[#CCFF00] hover:bg-[#CCFF00]/10 h-8 text-[10px] uppercase"
                data-testid={`group-attach-file-${group.name}`}
              >
                <Paperclip size={12} className="mr-1" /> Subir archivo
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (!f) return;
                  setUploading(true);
                  await onUpload(f);
                  setUploading(false);
                }}
                data-testid={`group-attach-file-input-${group.name}`}
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (!f) return;
                  setUploading(true);
                  await onUpload(f);
                  setUploading(false);
                }}
                data-testid={`group-attach-camera-input-${group.name}`}
              />
            </div>
          )}
        </div>
        {uploading && (
          <p className="text-[10px] text-gray-400 italic mb-2 flex items-center gap-1">
            <span className="w-3 h-3 border border-[#CCFF00] border-t-transparent rounded-full animate-spin" /> Subiendo…
          </p>
        )}
        {(!group.attachments || group.attachments.length === 0) ? (
          <p className="text-xs text-gray-500 italic">Sin adjuntos. {canManage ? "Sube reglas, autorizaciones o fotos del sorteo." : ""}</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {group.attachments.map((a) => {
              const isImg = a.kind === "photo" || (a.mime_type || "").startsWith("image/");
              const Icon = isImg ? ImageIcon : (a.mime_type === "application/pdf" ? FilePdf : FileText);
              return (
                <div
                  key={a.id}
                  className="relative group card-tactical p-2 hover:border-[#CCFF00]/40 transition-colors"
                  data-testid={`group-attachment-${a.id}`}
                >
                  <button
                    type="button"
                    onClick={() => isImg ? setPreviewAtt(a) : window.open(absUrl(a.url), "_blank")}
                    className="block w-full text-left"
                  >
                    {isImg ? (
                      <div className="aspect-square bg-black/40 rounded overflow-hidden mb-1 border border-white/5">
                        <img src={absUrl(a.url)} alt={a.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = 0.3; }} />
                      </div>
                    ) : (
                      <div className="aspect-square bg-black/40 rounded flex items-center justify-center mb-1 border border-white/5">
                        <Icon size={40} weight="light" className="text-gray-400" />
                      </div>
                    )}
                    <p className="text-[10px] text-white truncate font-medium">{a.name}</p>
                    <p className="text-[9px] text-gray-500">{humanSize(a.size)}</p>
                  </button>
                  <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <a
                      href={absUrl(a.url)}
                      download={a.name}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-6 h-6 bg-black/80 border border-white/20 rounded flex items-center justify-center text-gray-200 hover:text-[#CCFF00]"
                      title="Descargar"
                    >
                      <DownloadSimple size={12} />
                    </a>
                    {canManage && (
                      <button
                        type="button"
                        onClick={() => onDeleteAttachment(a.id)}
                        className="w-6 h-6 bg-black/80 border border-red-500/40 rounded flex items-center justify-center text-red-400 hover:bg-red-500/20"
                        title="Eliminar"
                        data-testid={`group-attachment-delete-${a.id}`}
                      >
                        <Trash size={12} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Image preview overlay */}
      {previewAtt && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewAtt(null)}
          data-testid="group-attachment-preview"
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 bg-black/80 border border-white/20 rounded flex items-center justify-center text-white"
            onClick={(e) => { e.stopPropagation(); setPreviewAtt(null); }}
          >
            <X size={20} />
          </button>
          <img src={absUrl(previewAtt.url)} alt={previewAtt.name} className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

// ============================================================
// Create / Edit group dialog
// ============================================================
function GroupFormDialog({ open, onClose, onSubmit, initial, users, defaultCategory }) {
  const [name, setName] = useState(initial?.name || "A");
  const [mode, setMode] = useState(initial?.mode || "pairs");
  const [category, setCategory] = useState(initial?.category || defaultCategory || "");

  // Normalized entries: each player = { user_id: string|null, name: string, guest: bool }
  const initialEntries = () => {
    if (initial?.entries) {
      return initial.entries.map((e) => ({
        players: (e.players || []).map((p) => ({
          user_id: p.user_id || null,
          name: p.name || "",
          guest: !!p.guest || !p.user_id,
        })),
      }));
    }
    const blank = mode === "pairs"
      ? [{ user_id: null, name: "", guest: false }, { user_id: null, name: "", guest: false }]
      : [{ user_id: null, name: "", guest: false }];
    return [{ players: blank.map((p) => ({ ...p })) }, { players: blank.map((p) => ({ ...p })) }, { players: blank.map((p) => ({ ...p })) }];
  };
  const [entries, setEntries] = useState(initialEntries);

  const usersById = useMemo(() => Object.fromEntries((users || []).map((u) => [u.id, u])), [users]);
  const isEdit = !!initial;

  const setPlayer = (idx, slot, patch) => {
    setEntries((prev) => prev.map((e, i) => {
      if (i !== idx) return e;
      const players = [...e.players];
      players[slot] = { ...players[slot], ...patch };
      return { ...e, players };
    }));
  };
  const addEntry = () => {
    if (entries.length >= 12) { toast.error("Máximo 12 entradas"); return; }
    const blank = mode === "pairs"
      ? [{ user_id: null, name: "", guest: false }, { user_id: null, name: "", guest: false }]
      : [{ user_id: null, name: "", guest: false }];
    setEntries((prev) => [...prev, { players: blank }]);
  };
  const removeEntry = (idx) => {
    if (entries.length <= 2) { toast.error("Mínimo 2 entradas"); return; }
    setEntries((prev) => prev.filter((_, i) => i !== idx));
  };
  const changeMode = (m) => {
    setMode(m);
    setEntries((prev) => prev.map((e) => {
      let players = [...(e.players || [])];
      if (m === "pairs" && players.length < 2) {
        players.push({ user_id: null, name: "", guest: false });
      }
      if (m === "individual") players = players.slice(0, 1);
      return { players };
    }));
  };

  const submit = () => {
    for (const [i, e] of entries.entries()) {
      const need = mode === "pairs" ? 2 : 1;
      const filled = (e.players || []).filter((p) => (p.user_id && p.user_id.length > 0) || (p.name && p.name.trim().length > 0));
      if (filled.length !== need) {
        toast.error(`Entrada ${i + 1}: completa ${need} jugador${need > 1 ? "es" : ""} (registrados o manuales)`);
        return;
      }
    }
    const payload = {
      name, category,
      entries: entries.map((e) => ({
        players: e.players.map((p) => ({
          user_id: p.user_id || null,
          name: (p.name || "").trim(),
          guest: !!p.guest || !p.user_id,
        })),
      })),
    };
    if (!isEdit) payload.mode = mode;
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="group-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] text-3xl font-black uppercase tracking-tighter">
            {isEdit ? `Editar grupo ${initial.name}` : "Nuevo grupo"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] uppercase mb-1 block tracking-widest">Grupo</Label>
              <Input value={name} onChange={(e) => setName(e.target.value.toUpperCase().slice(0, 3))} className="bg-black/60 border-white/15 h-10 text-center font-black text-2xl" placeholder="A" data-testid="group-form-name" />
            </div>
            <div>
              <Label className="text-[10px] uppercase mb-1 block tracking-widest">Categoría</Label>
              <Input value={category} onChange={(e) => setCategory(e.target.value)} className="bg-black/60 border-white/15 h-10" placeholder="Ej: Tercera" data-testid="group-form-category" />
            </div>
            <div>
              <Label className="text-[10px] uppercase mb-1 block tracking-widest">Modo</Label>
              <div className="flex gap-1">
                <Button type="button" onClick={() => !isEdit && changeMode("pairs")} disabled={isEdit} variant={mode === "pairs" ? "default" : "outline"} className={`flex-1 h-10 text-[10px] uppercase font-bold ${mode === "pairs" ? "bg-[#CCFF00] text-black hover:bg-[#B3E600]" : "border-white/15 text-gray-300"}`} data-testid="group-form-mode-pairs"><Users size={12} className="mr-1" /> Parejas</Button>
                <Button type="button" onClick={() => !isEdit && changeMode("individual")} disabled={isEdit} variant={mode === "individual" ? "default" : "outline"} className={`flex-1 h-10 text-[10px] uppercase font-bold ${mode === "individual" ? "bg-[#CCFF00] text-black hover:bg-[#B3E600]" : "border-white/15 text-gray-300"}`} data-testid="group-form-mode-individual"><UserIcon size={12} className="mr-1" /> Individual</Button>
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-[10px] uppercase tracking-widest">Entradas ({entries.length}) · genera {(entries.length * (entries.length - 1)) / 2} partidos round-robin</Label>
              <Button type="button" size="sm" onClick={addEntry} variant="outline" className="border-[#CCFF00]/30 text-[#CCFF00] h-7 text-[10px] uppercase" data-testid="group-form-add-entry">
                <Plus size={12} className="mr-1" /> Agregar
              </Button>
            </div>
            <p className="text-[10px] text-gray-500 italic mb-2">Puedes elegir jugadores registrados de la lista o marcar "Manual" para agregar jugadores invitados que no están en la plataforma.</p>
            <div className="space-y-2">
              {entries.map((e, idx) => (
                <div key={idx} className="p-3 bg-black/40 border border-white/10 rounded" data-testid={`group-form-entry-${idx}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="inline-flex items-center gap-2 text-xs uppercase font-bold text-gray-300">
                      <span className="w-6 h-6 bg-[#1E40AF] flex items-center justify-center text-white font-black text-xs rounded">{idx + 1}</span>
                      Entrada {idx + 1}
                    </span>
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeEntry(idx)} className="text-red-400 hover:bg-red-500/10 h-7 px-2 text-[10px]" data-testid={`group-form-remove-${idx}`}>
                      <X size={12} className="mr-1" /> Quitar
                    </Button>
                  </div>
                  <div className={`grid gap-2 ${mode === "pairs" ? "sm:grid-cols-2" : "grid-cols-1"}`}>
                    {(e.players || []).slice(0, mode === "pairs" ? 2 : 1).map((p, slot) => (
                      <PlayerPicker
                        key={slot}
                        value={p}
                        onChange={(patch) => setPlayer(idx, slot, patch)}
                        users={users}
                        usersById={usersById}
                        slotLabel={mode === "pairs" ? `Jugador ${slot + 1}` : "Jugador"}
                        testIdBase={`group-form-entry-${idx}-p${slot + 1}`}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-white/15 text-gray-300 uppercase text-xs" data-testid="group-form-cancel">Cancelar</Button>
          <Button onClick={submit} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="group-form-submit">
            {isEdit ? "Guardar" : "Crear grupo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// PlayerPicker — toggle between registered dropdown and manual name input
function PlayerPicker({ value, onChange, users, usersById, slotLabel, testIdBase }) {
  const isGuest = value?.guest || (!value?.user_id && !!value?.name);
  const mode = isGuest ? "manual" : "registered";
  const setMode = (m) => {
    if (m === "registered") onChange({ guest: false, user_id: value?.user_id || null, name: value?.name || "" });
    else onChange({ guest: true, user_id: null, name: value?.name || "" });
  };
  return (
    <div className="space-y-1" data-testid={`${testIdBase}-picker`}>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => setMode("registered")}
          className={`flex-1 py-1 text-[9px] uppercase tracking-widest font-bold border transition-colors ${mode === "registered" ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-black/40 text-gray-400 border-white/10 hover:border-white/25"}`}
          data-testid={`${testIdBase}-mode-registered`}
        >
          🎾 Registrado
        </button>
        <button
          type="button"
          onClick={() => setMode("manual")}
          className={`flex-1 py-1 text-[9px] uppercase tracking-widest font-bold border transition-colors ${mode === "manual" ? "bg-[#FFD24A] text-black border-[#FFD24A]" : "bg-black/40 text-gray-400 border-white/10 hover:border-white/25"}`}
          data-testid={`${testIdBase}-mode-manual`}
        >
          ✋ Manual / Invitado
        </button>
      </div>
      {mode === "registered" ? (
        <Select
          value={value?.user_id || undefined}
          onValueChange={(uid) => onChange({ user_id: uid, guest: false, name: usersById[uid]?.name || "" })}
        >
          <SelectTrigger className="bg-black/60 border-white/15 text-white text-xs h-9" data-testid={`${testIdBase}-select`}>
            <SelectValue placeholder={slotLabel} />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-white/10 text-white max-h-72">
            {(users || []).map((u) => (
              <SelectItem key={u.id} value={u.id} className="text-xs">{u.name} <span className="text-gray-500 ml-2">· {u.role}</span></SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <Input
          value={value?.name || ""}
          onChange={(e) => onChange({ user_id: null, guest: true, name: e.target.value })}
          placeholder={`${slotLabel} (invitado)`}
          className="bg-black/60 border-[#FFD24A]/30 text-white text-xs h-9"
          data-testid={`${testIdBase}-input`}
        />
      )}
    </div>
  );
}

// ============================================================
// Score result dialog
// ============================================================
function ScoreDialog({ open, onClose, group, match, onSaved }) {
  const initialSets = match?.status === "played" && match?.sets?.length ? match.sets : [{ a: "", b: "" }, { a: "", b: "" }];
  const [sets, setSets] = useState(initialSets);
  const [saving, setSaving] = useState(false);

  const a = group.entries.find((e) => e.seed === match?.a_seed);
  const b = group.entries.find((e) => e.seed === match?.b_seed);

  const addSet = () => setSets((prev) => [...prev, { a: "", b: "" }]);
  const removeSet = (i) => setSets((prev) => prev.filter((_, idx) => idx !== i));
  const setSet = (i, side, v) => setSets((prev) => prev.map((s, idx) => (idx === i ? { ...s, [side]: v } : s)));

  const submit = async () => {
    const clean = sets.filter((s) => s.a !== "" && s.b !== "").map((s) => ({ a: Number(s.a), b: Number(s.b) }));
    if (clean.length === 0) { toast.error("Captura al menos un set"); return; }
    setSaving(true);
    try {
      await api.post(`/groups/${group.id}/matches/${match.round_key}/result`, { sets: clean });
      toast.success("Resultado guardado");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const clearResult = async () => {
    if (!window.confirm("¿Borrar el resultado de este partido?")) return;
    try {
      await api.delete(`/groups/${group.id}/matches/${match.round_key}/result`);
      toast.success("Resultado borrado");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md" data-testid="score-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] text-2xl font-black uppercase">Marcador · {a?.display_name} vs {b?.display_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center px-2">
            <p className="text-xs uppercase text-[#CCFF00] font-bold text-center">{a?.display_name || `Seed ${a?.seed}`}</p>
            <span className="text-gray-500 text-xs">vs</span>
            <p className="text-xs uppercase text-[#CCFF00] font-bold text-center">{b?.display_name || `Seed ${b?.seed}`}</p>
          </div>
          {sets.map((s, i) => (
            <div key={i} className="grid grid-cols-[1fr_auto_1fr_auto] gap-2 items-center" data-testid={`score-set-${i}`}>
              <Input type="number" value={s.a} onChange={(e) => setSet(i, "a", e.target.value)} className="bg-black/60 border-white/15 text-center text-xl font-black h-12" data-testid={`score-set-${i}-a`} />
              <span className="text-gray-500">–</span>
              <Input type="number" value={s.b} onChange={(e) => setSet(i, "b", e.target.value)} className="bg-black/60 border-white/15 text-center text-xl font-black h-12" data-testid={`score-set-${i}-b`} />
              {sets.length > 1 && <Button size="sm" variant="ghost" onClick={() => removeSet(i)} className="text-red-400 h-8 w-8 p-0"><X size={14} /></Button>}
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addSet} className="border-white/15 text-gray-300 h-8 text-[10px] uppercase w-full" data-testid="score-add-set"><Plus size={12} className="mr-1" /> Agregar set</Button>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          {match?.status === "played" && (
            <Button variant="outline" onClick={clearResult} className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs uppercase" data-testid="score-clear">
              <Trash size={12} className="mr-1" /> Borrar
            </Button>
          )}
          <Button variant="outline" onClick={onClose} className="border-white/15 text-gray-300 uppercase text-xs" data-testid="score-cancel">Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="score-save">
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Shareable text (WhatsApp / copy)
// ============================================================
function buildShareText(g, eventName) {
  const lines = [];
  lines.push(`🏆 ${eventName || (g.event_kind === "tournament" ? "Torneo" : "Liga")}`);
  if (g.category) lines.push(`Categoría: ${g.category}`);
  lines.push(`Grupo ${g.name} · ${g.mode === "pairs" ? "Parejas" : "Individual"}`);
  lines.push("");
  g.entries.forEach((e) => lines.push(`${e.seed}. ${e.display_name || "—"}`));
  lines.push("");
  lines.push("📅 CALENDARIO");
  g.matches.forEach((m) => {
    const when = m.scheduled_at ? new Date(m.scheduled_at).toLocaleString("es-MX", { weekday: "short", hour: "2-digit", minute: "2-digit" }).toUpperCase() : "— por definir —";
    lines.push(`${m.a_seed} vs ${m.b_seed}   ${when}`);
  });
  const played = g.matches.filter((m) => m.status === "played");
  if (played.length > 0) {
    lines.push("");
    lines.push("📊 RESULTADOS");
    played.forEach((m) => lines.push(`${m.a_seed} vs ${m.b_seed}: ${m.a_sets}-${m.b_sets}`));
    lines.push("");
    lines.push("🥇 CLASIFICACIÓN");
    g.standings.forEach((s) => {
      const entry = g.entries.find((e) => e.seed === s.seed);
      lines.push(`#${s.position}. ${entry?.display_name || `Seed ${s.seed}`} · ${s.pts} pts`);
    });
  }
  if ((g.attachments || []).length > 0) {
    lines.push("");
    lines.push("📎 INFORMACIÓN ADJUNTA");
    g.attachments.forEach((a) => lines.push(`• ${a.name}: ${absUrl(a.url)}`));
  }
  return lines.join("\n");
}
